/**
 * Timeline maths shared by the preview, the timeline and the render request.
 * Mirrors VideoRenderService: a clip lasts (trimEnd - trimStart) / speed
 * (videos) or duration (images); clip k starts where clip k-1 ends minus
 * their transition overlap (a cut overlaps by one frame).
 */

import { AudioItem, AudioRole, Clip, FilterKey, StudioMusicTrack, TimelineLayout, TransitionKind } from "../../../types/videoType";

export const CUT = 0.04;
export const ANIM = 0.4;

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type AnimIn =
  | "NONE"
  | "POP"
  | "SLIDE_UP"
  | "SLIDE_DOWN"
  | "SLIDE_LEFT"
  | "SLIDE_RIGHT";

export type AnimOut = "NONE" | "FADE";

export interface GalleryItem {
  id: string | number;
  mediaType: "IMAGE" | "VIDEO" | string;
  mediaUrl: string;
  templateName?: string | null;
  fileName?: string | null;
}

export interface MediaMeta {
  duration?: number;
  width?: number;
  height?: number;
}

// export interface Clip {
//   id: string;
//   galleryId: string | number;
//   url: string;
//   image: boolean;
//   name: string;

//   sourceDuration: number;
//   width: number;
//   height: number;

//   trimStart: number;
//   trimEnd: number;
//   duration: number;
//   speed: number;

//   filter: FilterKey;

//   volume: number;
//   muted: boolean;

//   transition: TransitionKind;
//   transitionDuration: number;

//   kenBurns: boolean;
// }

export interface TimelineItem {
  clip: Clip;
  start: number;
  end: number;
  dur: number;
}

// export interface TimelineLayout {
//   items: TimelineItem[];
//   total: number;
// }

export interface FrameAtResult {
  k: number;
  local: number;
  next?: {
    k: number;
    local: number;
  };
  progress?: number;
  type?: TransitionKind;
}

export interface TransitionStyle {
  a: Record<string, string | number>;
  b: Record<string, string | number>;
  dip?: {
    background: string;
    opacity: number;
  };
}

export interface AnimLayer {
  animIn?: 
    | "NONE"
    | "FADE"
    | "POP"
    | "SLIDE_UP"
    | "SLIDE_DOWN"
    | "SLIDE_LEFT"
    | "SLIDE_RIGHT";

  animOut?: "NONE" | "FADE";

  start: number;
  end: number;
}

export interface AnimState {
  alpha: number;
  dx: number;
  dy: number;
  scale: number;
}

export type TransitionOption = readonly [
  TransitionKind,
  string
];

export type FilterOption = readonly [
  FilterKey,
  string,
  string
];

export const TRANSITIONS: readonly TransitionOption[] = [
  ["NONE", "Cut"],
  ["FADE", "Fade"],
  ["DISSOLVE", "Dissolve"],
  ["FADEBLACK", "Dip to black"],
  ["FADEWHITE", "Dip to white"],
  ["SLIDELEFT", "Slide left"],
  ["SLIDERIGHT", "Slide right"],
  ["SLIDEUP", "Slide up"],
  ["WIPELEFT", "Wipe left"],
  ["WIPERIGHT", "Wipe right"],
  ["SMOOTHLEFT", "Smooth left"],
  ["CIRCLEOPEN", "Circle"],
  ["RADIAL", "Clock"],
  ["PIXELIZE", "Pixelate"],
];

export const CLIP_FILTERS: readonly FilterOption[] = [
  ["NONE", "Original", "none"],
  ["VIVID", "Vivid", "saturate(1.35) contrast(1.06)"],
  ["WARM", "Warm", "sepia(0.18) saturate(1.15) hue-rotate(-8deg)"],
  ["COOL", "Cool", "saturate(1.05) hue-rotate(10deg) brightness(1.02)"],
  ["VINTAGE", "Vintage", "sepia(0.45) contrast(0.92) brightness(1.04)"],
  ["FADE", "Faded", "contrast(0.85) brightness(1.04) saturate(0.8)"],
  ["DRAMA", "Drama", "contrast(1.25) saturate(1.1)"],
  ["BW", "B&W", "grayscale(1)"],
];

/* -------------------------------------------------------------------------- */
/* Audio pieces (mirrors VideoRenderService.prepareAudio)                     */
/* -------------------------------------------------------------------------- */

/** A track added to the timeline: music fills the video and repeats; a voiceover plays once from `at`. */
export function newAudioItem(
  track: StudioMusicTrack,
  role: AudioRole,
  at: number,
  total: number,
): AudioItem {
  const len = Number(track.durationSec) || 0;
  const music = role === "music";
  const start = music ? 0 : clamp(at, 0, Math.max(0, total - 0.5));
  const room = Math.max(0.5, total - start);
  return {
    id: uid("a"),
    trackId: track.id,
    url: track.url,
    title: track.title || (music ? "Music" : "Voiceover"),
    role,
    sourceDuration: len,
    at: r1(start),
    from: 0,
    dur: r1(music || !len ? room : Math.min(len, room)),
    loop: music,
    toEnd: music,
    volume: music ? 0.8 : 1,
    fadeIn: 0,
    fadeOut: music ? 1.5 : 0,
  };
}

/** Longest a piece can be from its in-point: the file's rest, unless it repeats. */
export const audioMaxDur = (a: AudioItem): number =>
  a.loop || !a.sourceDuration ? Infinity : Math.max(0.1, a.sourceDuration - a.from);

/** Where in the file a piece is at video time tt (null when it isn't playing). */
export function audioSourceTime(a: AudioItem, tt: number): number | null {
  const local = tt - a.at;
  if (local < 0 || local >= a.dur) return null;
  const pos = a.from + local;
  return a.loop && a.sourceDuration > 0 ? pos % a.sourceDuration : pos;
}

/** Volume of a piece at video time tt, with its fade in and fade out (0..2). */
export function audioGainAt(a: AudioItem, tt: number): number {
  const local = tt - a.at;
  if (local < 0 || local >= a.dur) return 0;
  let g = a.volume;
  if (a.fadeIn > 0 && local < a.fadeIn) g *= local / a.fadeIn;
  const left = a.dur - local;
  if (a.fadeOut > 0 && left < a.fadeOut) g *= left / a.fadeOut;
  return clamp(g, 0, 2);
}

/** Cuts a piece in two at video time tt; null if tt is not at least 0.2 s inside it. */
export function splitAudio(a: AudioItem, tt: number): [AudioItem, AudioItem] | null {
  const local = r1(tt - a.at);
  if (local < 0.2 || a.dur - local < 0.2) return null;
  const from = a.loop && a.sourceDuration > 0 ? (a.from + local) % a.sourceDuration : a.from + local;
  return [
    { ...a, dur: local, fadeOut: 0, toEnd: false },
    { ...a, id: uid("a"), at: r1(a.at + local), from, dur: r1(a.dur - local), fadeIn: 0 },
  ];
}

export const filterCss = (key: FilterKey): string => {
  const found = CLIP_FILTERS.find((f) => f[0] === key);

  return found?.[2] ?? CLIP_FILTERS[0][2];
};

let seq = 0;

export const uid = (p = "c"): string =>
  `${p}${Date.now().toString(36)}${(seq++).toString(36)}`;

export function newClip(
  item: GalleryItem,
  meta?: MediaMeta,
): Clip {
  const image = item.mediaType === "IMAGE";

  const sourceDuration = meta?.duration ?? 0;

  return {
    id: uid("c"),
    galleryId: item.id,
    url: item.mediaUrl,
    image,

    name:
      item.templateName ||
      item.fileName ||
      (image ? "Photo" : "Video"),

    sourceDuration,
    width: meta?.width ?? 0,
    height: meta?.height ?? 0,

    trimStart: 0,

    trimEnd: image
      ? 0
      : Math.min(sourceDuration, 60),

    duration: 4,
    speed: 1,

    filter: "NONE",

    volume: 1,
    muted: false,

    transition: "NONE",
    transitionDuration: 0.6,

    kenBurns: true,
  };
}

/* -------------------------------------------------------------------------- */
/* Clip duration                                                              */
/* -------------------------------------------------------------------------- */

export const outDur = (c: Clip): number =>
  c.image
    ? c.duration
    : Math.max(
        0.5,
        (c.trimEnd - c.trimStart) / (c.speed || 1),
      );

/* -------------------------------------------------------------------------- */
/* Transition overlap                                                         */
/* -------------------------------------------------------------------------- */

/** Overlap between clip k-1 and clip k. */
export function joinDuration(
  clips: Clip[],
  k: number,
): number {
  const prev = clips[k - 1];
  const current = clips[k];

  if (!prev || !current || prev.transition === "NONE") {
    return CUT;
  }

  const limit =
    0.45 *
    Math.min(
      outDur(prev),
      outDur(current),
    );

  return Math.max(
    CUT,
    Math.min(
      prev.transitionDuration,
      limit,
    ),
  );
}

/* -------------------------------------------------------------------------- */
/* Timeline layout                                                            */
/* -------------------------------------------------------------------------- */

/** Where each clip sits on the output timeline. */
export function layout(
  clips: Clip[],
): TimelineLayout {
  const out: TimelineItem[] = [];

  let at = 0;

  clips.forEach((c, k) => {
    const d = outDur(c);

    const start =
      k === 0
        ? 0
        : at - joinDuration(clips, k);

    out.push({
      clip: c,
      start,
      end: start + d,
      dur: d,
    });

    at = start + d;
  });

  return {
    items: out,
    total: at,
  };
}

export function solidColorDataUrl(color: string, w = 1080, h = 1920): string {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, w, h);
  }
  return canvas.toDataURL("image/png");
}

export const BLANK_COLORS: string[] = [
  "#10231E", "#0B7A5A", "#1D6FA5", "#7C3AED",
  "#DB2777", "#F97316", "#111827", "#FFFFFF",
];

export function frameAt(
  lay: TimelineLayout,
  t: number,
): FrameAtResult | null {
  const { items } = lay;

  if (!items.length) {
    return null;
  }

  const tt = Math.max(
    0,
    Math.min(
      t,
      lay.total - 0.001,
    ),
  );

  for (let k = 0; k < items.length; k++) {
    const it = items[k];
    const next = items[k + 1];

    if (
      next &&
      tt >= next.start &&
      tt < it.end
    ) {
      // overlap: a transition from k to k+1

      const p =
        (tt - next.start) /
        Math.max(
          0.001,
          it.end - next.start,
        );

      return {
        k,
        local: tt - it.start,

        next: {
          k: k + 1,
          local: tt - next.start,
        },

        progress: p,
        type: it.clip.transition,
      };
    }

    if (
      tt >= it.start &&
      tt < it.end
    ) {
      return {
        k,
        local: tt - it.start,
      };
    }
  }

  const last = items.length - 1;

  return {
    k: last,
    local: items[last].dur,
  };
}

/* -------------------------------------------------------------------------- */
/* Source time                                                                */
/* -------------------------------------------------------------------------- */

/** The source time (seconds into the video file) for a clip at a local time. */
export const sourceTime = (
  c: Clip,
  local: number,
): number =>
  c.image
    ? 0
    : c.trimStart +
      local * (c.speed || 1);

/* -------------------------------------------------------------------------- */
/* Split clip                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Splits clip index k at output time t.
 * Returns the new clip list or null when too close to an edge.
 */
export function splitAt(
  clips: Clip[],
  k: number,
  local: number,
): Clip[] | null {
  const c = clips[k];

  if (!c) {
    return null;
  }

  const d = outDur(c);

  if (
    local < 0.5 ||
    d - local < 0.5
  ) {
    return null;
  }

  const a: Clip = {
    ...c,
    transition: "NONE",
  };

  const b: Clip = {
    ...c,
    id: uid("c"),
  };

  if (c.image) {
    a.duration =
      Math.round(local * 10) / 10;

    b.duration =
      Math.round((d - local) * 10) / 10;

    b.kenBurns = c.kenBurns;
  } else {
    const s =
      c.trimStart +
      local * c.speed;

    a.trimEnd = s;
    b.trimStart = s;
  }

  return [
    ...clips.slice(0, k),
    a,
    b,
    ...clips.slice(k + 1),
  ];
}

/* -------------------------------------------------------------------------- */
/* Transition styles                                                          */
/* -------------------------------------------------------------------------- */

/** CSS for the outgoing (A) and incoming (B) clip during a transition preview. */
export function transitionStyles(
  type: TransitionKind,
  p: number,
): TransitionStyle {
  const e =
    p * p * (3 - 2 * p); // smooth

  switch (type) {
    case "FADE":
    case "DISSOLVE":
      return {
        a: {},
        b: {
          opacity: e,
        },
      };

    case "FADEBLACK":
    case "FADEWHITE":
      return {
        a: {
          opacity: p < 0.5 ? 1 : 0,
        },

        b: {
          opacity: p < 0.5 ? 0 : 1,
        },

        dip: {
          background:
            type === "FADEBLACK"
              ? "#000"
              : "#fff",

          opacity:
            1 -
            Math.abs(p - 0.5) * 2,
        },
      };

    case "SLIDELEFT":
      return {
        a: {
          transform: `translateX(${-e * 100}%)`,
        },

        b: {
          transform: `translateX(${(1 - e) * 100}%)`,
        },
      };

    case "SLIDERIGHT":
      return {
        a: {
          transform: `translateX(${e * 100}%)`,
        },

        b: {
          transform: `translateX(${-(1 - e) * 100}%)`,
        },
      };

    case "SLIDEUP":
      return {
        a: {
          transform: `translateY(${-e * 100}%)`,
        },

        b: {
          transform: `translateY(${(1 - e) * 100}%)`,
        },
      };

    case "SMOOTHLEFT":
      return {
        a: {},

        b: {
          clipPath: `inset(0 0 0 ${(1 - e) * 100}%)`,
          opacity: Math.min(1, e * 1.6),
        },
      };

    case "WIPELEFT":
      return {
        a: {},

        b: {
          clipPath: `inset(0 0 0 ${(1 - e) * 100}%)`,
        },
      };

    case "WIPERIGHT":
      return {
        a: {},

        b: {
          clipPath: `inset(0 ${(1 - e) * 100}% 0 0)`,
        },
      };

    case "CIRCLEOPEN":
      return {
        a: {},

        b: {
          clipPath: `circle(${e * 75}% at 50% 50%)`,
        },
      };

    case "RADIAL":
      return {
        a: {},

        b: {
          WebkitMaskImage:
            `conic-gradient(#000 ${e * 360}deg, transparent 0)`,

          maskImage:
            `conic-gradient(#000 ${e * 360}deg, transparent 0)`,
        },
      };

    case "PIXELIZE":
      return {
        a: {
          filter: `blur(${Math.sin(p * Math.PI) * 8}px)`,
        },

        b: {
          opacity: p < 0.5 ? 0 : 1,

          filter:
            `blur(${Math.sin(p * Math.PI) * 8}px)`,
        },
      };

    case "NONE":
    default:
      return {
        a: {},
        b: {
          opacity: p > 0.5 ? 1 : 0,
        },
      };
  }
}

/* -------------------------------------------------------------------------- */
/* Animation                                                                  */
/* -------------------------------------------------------------------------- */

/** Text/sticker animation at time t (output seconds). */
export function animState(
  L: AnimLayer,
  t: number,
): AnimState {
  let alpha = 1;
  let dx = 0;
  let dy = 0;
  let scale = 1;

  const inD =
    L.animIn === "POP"
      ? 0.2
      : ANIM;

  const since = t - L.start;

  if (
    L.animIn &&
    L.animIn !== "NONE" &&
    since < ANIM
  ) {
    const p = Math.max(
      0,
      Math.min(
        1,
        since / ANIM,
      ),
    );

    alpha = Math.max(
      0,
      Math.min(
        1,
        since / inD,
      ),
    );

    const q = 1 - p;

    if (L.animIn === "SLIDE_UP") {
      dy = q * 0.08;
    }

    if (L.animIn === "SLIDE_DOWN") {
      dy = -q * 0.08;
    }

    if (L.animIn === "SLIDE_LEFT") {
      dx = q * 0.1;
    }

    if (L.animIn === "SLIDE_RIGHT") {
      dx = -q * 0.1;
    }

    if (L.animIn === "POP") {
      dy = q * 0.03;
      scale = 1 + q * 0.0;
    }
  }

  if (
    L.animOut === "FADE" &&
    L.end - L.start > ANIM * 2 &&
    t > L.end - ANIM
  ) {
    alpha *= Math.max(
      0,
      (L.end - t) / ANIM,
    );
  }

  return {
    alpha,
    dx,
    dy,
    scale,
  };
}

/* -------------------------------------------------------------------------- */
/* Formatting                                                                 */
/* -------------------------------------------------------------------------- */

export function fmt(s: number): string {
  if (!Number.isFinite(s)) {
    return "0:00.0";
  }

  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const t = Math.floor((s % 1) * 10);

  return `${m}:${String(sec).padStart(2, "0")}.${t}`;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

export const clamp = (
  v: number,
  a: number,
  b: number,
): number =>
  Math.min(
    b,
    Math.max(a, v),
  );

export const r1 = (
  v: number,
): number =>
  Math.round(v * 100) / 100;